import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

const Navbar: React.FC = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  const navItems = [
    { label: 'Doctrine', path: '/doctrine', isExternal: false },
    { label: 'Lore', path: '/lore', isExternal: false },
    { label: 'About Academy', path: '/about', isExternal: false },
    { label: 'Log In', path: '#', isExternal: true, isActionButton: true },
  ];

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  return (
    <>
      <nav 
        className="fixed top-0 left-0 right-0 z-50 flex justify-center pt-3 sm:pt-4 md:pt-6 px-4 sm:px-6"
      >
        <div 
          className="w-full max-w-7xl backdrop-blur-xl rounded-full pl-3 sm:pl-4 md:pl-6 lg:pl-8 pr-2 sm:pr-3 lg:pr-4"
          style={{ 
            backgroundColor: 'rgba(255, 255, 255, 0.04)',
            backdropFilter: 'blur(20px) saturate(180%)',
            WebkitBackdropFilter: 'blur(20px) saturate(180%)',
            boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.1)'
          }}
        >
          <div className="flex items-center justify-between h-12 sm:h-14 md:h-16">
            {/* Logo/Brand */}
            <Link 
              to="/" 
              className="flex items-center space-x-3 flex-shrink-0"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <img src="/symbol.svg" alt="Order of Architects" className="h-7 sm:h-8 md:h-9 w-auto drop-shadow-[0_0_6px_rgba(248,248,252,0.5)]" />
              <span className="jacquard-font text-lg sm:text-xl md:text-3xl lg:text-2xl xl:text-3xl text-white drop-shadow-[0_0_6px_rgba(248,248,252,0.5)] whitespace-nowrap">
                Order of Architects
              </span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center ml-8 xl:ml-12 gap-2 xl:gap-3">
              {navItems.map((item, index) => {
                const isActive = location.pathname === item.path;
                const isActionButton = (item as any).isActionButton;
                
                const baseClasses = isActionButton
                  ? "px-3 xl:px-4 py-2 text-sm xl:text-base font-sans font-bold transition-all duration-300 rounded-full ml-2 xl:ml-4 hover:scale-105 hover:shadow-lg"
                  : "px-3 xl:px-4 py-2 text-sm xl:text-base font-sans font-semibold transition-all duration-300 rounded-none";
                
                const activeClasses = isActionButton
                  ? "text-gray-900 hover:bg-gray-200 hover:shadow-gray-900/20"
                  : isActive 
                    ? "text-white" 
                    : "text-white/70 hover:text-white";
                
                const style = isActionButton ? { backgroundColor: '#F9F9F9' } : {};
                
                if (item.isExternal) {
                  return (
                    <a
                      key={index}
                      href={item.path}
                      className={`${baseClasses} ${activeClasses}`}
                      style={style}
                    >
                      {item.label}
                    </a>
                  );
                }

                return (
                  <Link
                    key={index}
                    to={item.path}
                    className={`${baseClasses} ${activeClasses}`}
                    style={style}
                  >
                    {item.label}
                  </Link>
                );
              })}
            </div>

            {/* Mobile/Tablet Menu Button */}
            <button
              onClick={toggleMobileMenu}
              className="lg:hidden p-2 text-white/70 hover:text-white transition-colors duration-300 flex-shrink-0"
              aria-label="Toggle menu"
            >
              <svg
                className="w-6 h-6 sm:w-7 sm:h-7"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                {isMobileMenuOpen ? (
                  <path d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile/Tablet Menu Overlay */}
      {isMobileMenuOpen && (
        <div 
          className="fixed inset-0 z-40 lg:hidden"
          onClick={toggleMobileMenu}
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
          
          {/* Menu Content */}
          <div 
            className="absolute top-0 left-0 right-0 mt-20 sm:mt-24 md:mt-28 mx-4 sm:mx-6 rounded-3xl backdrop-blur-xl z-50"
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.04)',
              backdropFilter: 'blur(20px) saturate(180%)',
              WebkitBackdropFilter: 'blur(20px) saturate(180%)',
              boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.1)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex flex-col p-4 sm:p-6 space-y-3 sm:space-y-4">
              {navItems.map((item, index) => {
                const isActive = location.pathname === item.path;
                const isActionButton = (item as any).isActionButton;
                
                const baseClasses = isActionButton
                  ? "px-4 sm:px-6 py-3 sm:py-4 text-base sm:text-lg font-sans font-bold transition-all duration-300 rounded-full text-center hover:scale-105 hover:shadow-lg"
                  : "px-4 sm:px-6 py-3 sm:py-4 text-base sm:text-lg font-sans font-semibold transition-all duration-300 rounded-none text-left";
                
                const activeClasses = isActionButton
                  ? "text-gray-900 hover:bg-gray-200 hover:shadow-gray-900/20"
                  : isActive 
                    ? "text-white" 
                    : "text-white/70 hover:text-white";
                
                const style = isActionButton ? { backgroundColor: '#F9F9F9' } : {};
                
                if (item.isExternal) {
                  return (
                    <a
                      key={index}
                      href={item.path}
                      className={`${baseClasses} ${activeClasses}`}
                      style={style}
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      {item.label}
                    </a>
                  );
                }

                return (
                  <Link
                    key={index}
                    to={item.path}
                    className={`${baseClasses} ${activeClasses}`}
                    style={style}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {item.label}
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;
